import { CommonModule, DOCUMENT } from '@angular/common';
import {
	ChangeDetectorRef,
	DestroyRef,
	Directive,
	ElementRef,
	EventEmitter,
	HostBinding,
	HostListener,
	inject,
	Input,
	NgModule,
	OnInit,
	Output,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { fromEvent } from 'rxjs';
import { filter, map } from 'rxjs/operators';

@Directive({
	// eslint-disable-next-line @angular-eslint/prefer-standalone
	standalone: false,
	selector: '[inputOnlyNumber]',
	exportAs: 'inputOnlyNumber',
})
export class ReachInputOnlyNumberDirective implements OnInit {
	private readonly destroyRef = inject(DestroyRef);
	private elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
	private cdRef = inject(ChangeDetectorRef);
	private document = inject(DOCUMENT);

	@Input()
	public fallback = 0;

	@Input()
	public allowNegativeValues = true;

	@Input()
	public allowDecimalValues = true;

	@Input()
	public minValue = Number.NEGATIVE_INFINITY;

	@Input()
	public maxValue = Number.POSITIVE_INFINITY;

	@HostBinding('attr.inputmode')
	@Input()
	public inputmode = 'decimal';

	private lastValidText = '0';

	@Output()
	public cleanInput = new EventEmitter<number>();

	private lastSelectionOffset = 0;
	private DECIMAL_POINT = '.';

	ngOnInit(): void {
		this.lastValidText = `${this.fallback || 0}`;
		this.lastSelectionOffset = this.lastValidText.length;

		fromEvent(this.document, 'selectionchange')
			.pipe(
				map(() => window.getSelection()),
				filter((selection) => {
					return selection?.focusNode === this.elementRef.nativeElement.childNodes[0];
				}),
				takeUntilDestroyed(this.destroyRef)
			)
			.subscribe((selection) => {
				this.lastSelectionOffset = selection?.focusOffset ?? 0;
			});
	}

	@HostListener('focus')
	public onFocus(): void {
		this.setSelection(this.lastValidText.length);
	}

	@HostListener('input', ['$event.target.innerText'])
	public onInput(text: string): void {
		// Get selection because we will need to reset it.
		let selectionOffset = window.getSelection()?.focusOffset ?? 0;

		if (text === '0-') {
			text = '-0';
			selectionOffset = text.length;
		}

		let cleanText = this.cleanText(text);
		let textAsNumber = Number(cleanText);

		if (textAsNumber < this.minValue) {
			cleanText = this.cleanText(`${this.minValue}`);
			textAsNumber = this.minValue;
		} else if (textAsNumber > this.maxValue) {
			cleanText = this.cleanText(`${this.maxValue}`);
			textAsNumber = this.maxValue;
		}

		const numberIsInvalid = this.valueIsInvalid(cleanText, textAsNumber);

		// If the current number is invalid, we reset the last one that was valid.
		// If it is valid, we update the last valid value to the current one.
		if (numberIsInvalid) {
			selectionOffset = this.lastSelectionOffset;
		} else {
			this.lastValidText = cleanText;
		}

		if (text === '') {
			selectionOffset = this.lastValidText.length;
		}

		// Set the new value.
		this.elementRef.nativeElement.innerText = this.lastValidText;
		this.cleanInput.emit(Number(this.lastValidText));

		// Reset pointer
		// (it automatically resets to 0 when you change the innerText for some reason).
		this.setSelection(selectionOffset);
		this.cdRef.detectChanges();
	}

	private setSelection(startRange: number): void {
		try {
			const rangeObj = this.document.createRange();

			const element = this.elementRef.nativeElement.childNodes[0];
			const maxRange = element.textContent?.length ?? 0;

			rangeObj.setStart(element, Math.min(startRange, maxRange));
			rangeObj.collapse(true);

			const selection = window.getSelection();
			selection?.removeAllRanges();
			selection?.addRange(rangeObj);
		} catch (e) {
			console.error(e);
		}
	}

	private cleanText(text: string): string {
		const clean = (text || '0')
			.trim()
			.replace(/,/, this.DECIMAL_POINT)
			.replace(/\./, this.DECIMAL_POINT);

		const match = /(-?[0-9]+)(\.[0-9]*)?/.exec(clean);

		const firstMatch = match?.[1];
		const firstNumAbs = Math.abs(Number(match?.[1]));
		const signSymbol = firstMatch?.startsWith('-') ? '-' : '';
		const firstPart = `${signSymbol}${firstNumAbs}`;
		const secondPart = match?.[2] ?? '';

		const cleanText = `${firstPart}${secondPart}`;

		return cleanText;
	}

	private valueIsInvalid(str: string, num: number): boolean {
		const numIsNan = Number.isNaN(num);

		const isDecimal = !Number.isInteger(num) || str.includes(this.DECIMAL_POINT);
		const invalidDecimal = !this.allowDecimalValues && isDecimal;

		const isNegative = num < 0 || str.includes('-');
		const invalidNegative = !this.allowNegativeValues && isNegative;

		return numIsNan || invalidDecimal || invalidNegative;
	}
}

@NgModule({
	declarations: [ReachInputOnlyNumberDirective],
	imports: [CommonModule],
	exports: [ReachInputOnlyNumberDirective],
})
export class ReachInputOnlyNumberModule {}
